import {
  CARACAS_NEIGHBORHOOD_VALUES,
  PANAMA_NEIGHBORHOOD_VALUES,
  type Neighborhood,
} from '../api/schemas';

/**
 * Where a business operates. Panamá and Venezuela have Dale zones: every zone
 * belongs to one city, and the country can be read off it
 * (services/api/src/business-country.ts). Since 2 Nov 2026 a business can be
 * anywhere in the world (Martín): outside those two it writes its city or
 * area, drops its pin and signs up with its own country code. Panamá is the
 * default when neither the link nor the device says otherwise.
 */
export type CountryCode = 'PA' | 'VE';
/** Any ISO 3166-1 alpha-2 code, upper case: "US", "ES". Panamá and Venezuela included. */
export type SignupCountry = string;

/** Every ISO 3166-1 alpha-2 country, for the "Otro país" list. */
const ISO_COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO ' +
  'FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE ' +
  'JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO ' +
  'MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW ' +
  'PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM ' +
  'TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');
const ISO_COUNTRIES: ReadonlySet<string> = new Set(ISO_COUNTRY_CODES);

const SPANISH_NAMES = new Intl.DisplayNames(['es'], { type: 'region' });

/** "Estados Unidos", "España"; the code itself if the browser cannot name it. */
export function countryName(code: string): string {
  return SPANISH_NAMES.of(code) ?? code;
}

export function isWorldCountry(code: string): boolean {
  return ISO_COUNTRIES.has(code);
}

/** Panamá and Venezuela: the two countries with Dale zones, phone shapes and a fixed clock. */
export function isZonedCountry(code: string): code is CountryCode {
  return code === 'PA' || code === 'VE';
}

/** Every country but Panamá and Venezuela, by Spanish name: the "Otro país" list. */
export const WORLD_COUNTRIES: readonly { value: SignupCountry; label: string }[] = ISO_COUNTRY_CODES.filter(
  (code) => !isZonedCountry(code)
)
  .map((code) => ({ value: code, label: countryName(code) }))
  .sort((left, right) => left.label.localeCompare(right.label, 'es'));

/** What the device says about where the person is, read once when the signup opens. */
export type DeviceHints = { timeZone?: string; language?: string };

export function currentDevice(): DeviceHints {
  try {
    return {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: typeof navigator === 'undefined' ? undefined : navigator.language,
    };
  } catch {
    return {};
  }
}

/** The region of a language tag ("en-US" is US); none for "es" or "es-419". */
function regionOf(language: string | undefined): string | undefined {
  if (!language) return undefined;
  const region = language.split(/[-_]/).slice(1).find((part) => /^[A-Za-z]{2}$/.test(part));
  return region?.toUpperCase();
}

export const DEFAULT_COUNTRY: CountryCode = 'PA';

export const COUNTRY_OPTIONS: readonly { value: CountryCode; label: string }[] = [
  { value: 'PA', label: 'Panamá' },
  { value: 'VE', label: 'Venezuela' },
];

/** Mirrors COUNTRY_RULES in services/api/src/business-country.ts. */
export const COUNTRY_TIMEZONE: Readonly<Record<CountryCode, string>> = {
  PA: 'America/Panama',
  VE: 'America/Caracas',
};

const ZONES: Readonly<Record<CountryCode, readonly Neighborhood[]>> = {
  PA: PANAMA_NEIGHBORHOOD_VALUES,
  VE: CARACAS_NEIGHBORHOOD_VALUES,
};

type PhoneRule = { pattern: RegExp; hint: string; error: string; placeholder: string };

/** Same shapes the API enforces: +507 with 7 or 8 digits, +58 with 10. */
const PHONE_RULES: Readonly<Record<CountryCode, PhoneRule>> = {
  PA: {
    pattern: /^\+507[2-9]\d{6,7}$/,
    hint: 'Opcional, +507',
    error: 'Escribe el teléfono con +507 y 7 u 8 dígitos.',
    placeholder: '+507 6000 0000',
  },
  VE: {
    pattern: /^\+58[24]\d{9}$/,
    hint: 'Opcional, +58 y 10 dígitos',
    error: 'Escribe el teléfono con +58 y 10 dígitos, por ejemplo +58 412 1234567.',
    placeholder: '+58 412 1234567',
  },
};

/**
 * The country a signup starts in: `?pais=` when the link says one (any country
 * code), else the device clock in Caracas or Panama, else the region of the
 * device language ("en-US" is the United States), else Panamá.
 */
export function readCountryChoice(search: string, device: DeviceHints = {}): SignupCountry {
  const value = new URLSearchParams(search).get('pais')?.trim().toUpperCase() ?? '';
  if (isWorldCountry(value)) return value;
  if (device.timeZone === 'America/Caracas') return 'VE';
  if (device.timeZone === 'America/Panama') return 'PA';
  const region = regionOf(device.language);
  return region && isWorldCountry(region) ? region : DEFAULT_COUNTRY;
}

/** The Dale zones of Panamá or Venezuela; none anywhere else, where the business writes its own area. */
export function zonesFor(country: SignupCountry): readonly Neighborhood[] {
  return isZonedCountry(country) ? ZONES[country] : [];
}

export function countryOfZone(zone: string): CountryCode {
  return (CARACAS_NEIGHBORHOOD_VALUES as readonly string[]).includes(zone) ? 'VE' : 'PA';
}

/** Google Maps region code for a zone's country. */
export function mapRegionOf(zone: string): CountryCode {
  return countryOfZone(zone);
}

/** Abroad any international number, written with its own + code: the CRM cannot guess it. */
const WORLD_PHONE_RULE: PhoneRule = {
  pattern: /^\+[1-9]\d{7,14}$/,
  hint: 'Opcional, con + y el código de tu país',
  error: 'Escribe el teléfono con + y el código de tu país, por ejemplo +1 305 555 0100.',
  placeholder: '+1 305 555 0100',
};

export function phoneRuleFor(country: SignupCountry): PhoneRule {
  return isZonedCountry(country) ? PHONE_RULES[country] : WORLD_PHONE_RULE;
}

/** Spaces and dashes out: "+58 412-123 4567" is "+584121234567". */
export function normalizePhone(raw: string): string {
  return raw.trim().replace(/[\s-]/g, '');
}

/** Undefined when the phone fits the country; the message to show otherwise. */
export function phoneErrorFor(country: SignupCountry, phoneE164: string): string | undefined {
  const rule = phoneRuleFor(country);
  return rule.pattern.test(phoneE164) ? undefined : rule.error;
}
